/**
 * Stats fetch for a grid page's summary cards.
 *
 * A grid's stat cards read a sibling /stats/ endpoint scoped by the same
 * params as the list, refetching whenever those params change. This hook
 * owns that fetch once — cancel-guarded against out-of-order responses,
 * loading tracked, errors collapsing to null — instead of every page
 * hand-writing the same effect.
 *
 * Contains:
 * - useGridStats — (fetchStats, params, opts) → { stats, isLoading }
 */

import { useEffect, useState } from 'react';

/**
 * @param {(params: object) => Promise<object>} fetchStats - Stats fetcher
 *   (a stable named api function).
 * @param {object} params - Wire-ready query params. Must be referentially
 *   stable between renders (memoized by the caller) — a fresh object every
 *   render refetches every render.
 * @param {object} [opts]
 * @param {boolean} [opts.enabled] - When false, no fetch runs and the last
 *   stats value is kept.
 * @returns {{ stats: object|null, isLoading: boolean }}
 */
export default function useGridStats(fetchStats, params, { enabled = true } = {}) {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setIsLoading(true);
    fetchStats(params)
      .then(data => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchStats, params, enabled]);

  return { stats, isLoading };
}
